import { ApiEndpointClient } from './resource-client';
import { parseApiError } from './http/error';
const AI_API_PREFIX = 'ai-api/v1';
const ASYNC_AI_POLLING_INTERVAL_MS = 3_000;
export class AiClient extends ApiEndpointClient {
    http;
    constructor(http) {
        super(http, AI_API_PREFIX);
        this.http = http;
    }
    invokeWorkflowWithAsyncPolling = async ({ agentUseCase, orderId, request, pollingIntervalMs = ASYNC_AI_POLLING_INTERVAL_MS, onProgress, onPartialResult, signal, }) => {
        const deliverProgress = createProgressDelivery({ onProgress, onPartialResult });
        const lifecycle = await startAsyncInvocation(() => this.post('/async-invocations/', {
            headers: {
                Accept: 'text/plain, application/json',
            },
            json: buildWorkflowPayload({ agentUseCase, request }),
            response: 'raw',
            query: orderId ? { order_uuid: orderId } : undefined,
            signal,
        }));
        let latestProgress = lifecycle.progress;
        deliverProgress(latestProgress);
        if (lifecycle.status === 'failed') {
            throw new AsyncAiInvocationFailedError(getAsyncInvocationErrorMessage(lifecycle), true);
        }
        const pollPath = lifecycle.pollUrl ? `/${lifecycle.pollUrl.replace(/^\/+/, '')}` : `/async-invocations/${lifecycle.invocationId}/`;
        const pollQuery = lifecycle.pollUrl || !orderId ? undefined : { order_uuid: orderId };
        while (true) {
            await wait(pollingIntervalMs, signal);
            const pollResponse = await this.get(pollPath, {
                response: 'raw',
                headers: {
                    Accept: 'text/plain, application/json',
                },
                signal,
                query: pollQuery,
            });
            await throwOnHttpError(pollResponse);
            if (pollResponse.status === 202) {
                const pendingLifecycle = parseAsyncInvocationLifecycle(await pollResponse.json());
                if (pendingLifecycle.progress) {
                    latestProgress = pendingLifecycle.progress;
                }
                deliverProgress(pendingLifecycle.progress);
                if (pendingLifecycle.status === 'failed') {
                    throw new AsyncAiInvocationFailedError(getAsyncInvocationErrorMessage(pendingLifecycle), true);
                }
                continue;
            }
            const responseText = await pollResponse.text();
            const responseJson = tryParseJson(responseText);
            const failedLifecycle = parseTerminalFailedLifecycle(responseJson);
            if (failedLifecycle) {
                deliverProgress(failedLifecycle.progress);
                throw new AsyncAiInvocationFailedError(getAsyncInvocationErrorMessage(failedLifecycle), true);
            }
            deliverProgress(finalizeSuccessfulProgress(latestProgress));
            return {
                response: responseText,
            };
        }
    };
}
export class AsyncAiInvocationFailedError extends Error {
    isTerminalWorkflowFailure;
    constructor(message, isTerminalWorkflowFailure = false) {
        super(message);
        this.isTerminalWorkflowFailure = isTerminalWorkflowFailure;
        this.name = 'AsyncAiInvocationFailedError';
    }
}
const buildWorkflowPayload = ({ agentUseCase, request, }) => ({
    agentUseCase,
    inputText: request.prompt.trim(),
    sessionState: request.files?.length ? { files: request.files } : undefined,
});
const wait = (ms, signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) {
        reject(signal.reason);
        return;
    }
    const onAbort = () => {
        clearTimeout(timeoutId);
        reject(signal?.reason);
    };
    const timeoutId = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
});
const tryParseJson = (value) => {
    try {
        return JSON.parse(value);
    }
    catch {
        return undefined;
    }
};
const startAsyncInvocation = async (request) => {
    const response = await request();
    await throwOnHttpError(response);
    return parseAsyncInvocationLifecycle(await response.json());
};
const parseAsyncInvocationLifecycle = (value) => {
    if (isLifecycle(value)) {
        return value;
    }
    throw new AsyncAiInvocationFailedError('Async AI invocation returned a malformed lifecycle envelope', false);
};
const parseTerminalFailedLifecycle = (value) => {
    if (isLifecycle(value) && value.status === 'failed') {
        return value;
    }
    if (isRecord(value) && value.status === 'failed' && 'invocationId' in value) {
        throw new AsyncAiInvocationFailedError('Async AI invocation returned a malformed lifecycle envelope', false);
    }
};
const isRecord = (value) => typeof value === 'object' && value !== null;
const isLifecycle = (value) => isRecord(value) &&
    typeof value.invocationId === 'string' &&
    typeof value.expiresAt === 'string' &&
    (value.status === 'pending' || (value.status === 'failed' && typeof value.error === 'string'));
const throwOnHttpError = async (response) => {
    if (!response.ok) {
        throw await parseApiError(response, response.url || AI_API_PREFIX);
    }
};
const getAsyncInvocationErrorMessage = (lifecycle) => lifecycle.error;
const finalizeSuccessfulProgress = (progress) => {
    const hasUnfinishedSteps = progress?.steps.some(({ status }) => ['pending', 'running', 'retrying'].includes(status));
    if (!progress || !hasUnfinishedSteps) {
        return undefined;
    }
    return {
        ...progress,
        sequence: progress.sequence + 1,
        steps: progress.steps.map((step) => ({
            ...step,
            status: ['pending', 'running', 'retrying'].includes(step.status) ? 'completed' : step.status,
        })),
    };
};
const createProgressDelivery = ({ onProgress, onPartialResult, }) => {
    let highestSequence = 0;
    const partialRevisions = new Map();
    return (progress) => {
        if (!progress || progress.sequence <= highestSequence) {
            return;
        }
        highestSequence = progress.sequence;
        onProgress?.(progress);
        const stepOrder = new Map(progress.steps.map((step, index) => [step.id, index]));
        const partialResults = [...progress.partial_results].sort((left, right) => (stepOrder.get(left.step_id) ?? progress.steps.length) - (stepOrder.get(right.step_id) ?? progress.steps.length));
        for (const partialResult of partialResults) {
            const currentRevision = partialRevisions.get(partialResult.step_id) ?? 0;
            if (partialResult.revision <= currentRevision) {
                continue;
            }
            partialRevisions.set(partialResult.step_id, partialResult.revision);
            onPartialResult?.(partialResult);
        }
    };
};
